<template>
  <Modal>
    <Modal.Title>Make selected files public</Modal.Title>
    <Modal.Body>
      <UpgradeToProCallout v-if="isBasic" class="mb-5">
        Enable privacy/invite controls and other features
      </UpgradeToProCallout>

      <div :class="containerClasses">
        You are about to change the selected files to be public. Anyone will be
        able to view these files and their descriptions. This may take a few
        moments to complete.
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="stroke"
        :size="isMobile ? 'lg' : 'md'"
        :disabled="loading || isBasic"
        @click="close()"
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        :size="isMobile ? 'lg' : 'md'"
        :disabled="isBasic"
        :loading="loading"
        @click="handleClick"
      >
        Make public
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Modal, Button, useModal } from '@dacr/library'
import { openModal, Modals } from '@/shared/dialogs/modals'
import {
  UserPermission,
  useUpdateFilePrivacyMutation,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { updateFileInExplorerCache } from '@/features/explorer/utils/explorerCache'
import gql from 'graphql-tag'
import { useUserStore } from '@/shared/stores/user'
import { useDevice } from '@/shared/composables/useDevice'
import UpgradeToProCallout from '@/features/billing/components/UpgradeToProCallout.vue'

const { close } = useModal()
const { isMobile } = useDevice()

const props = defineProps<{
  ids: string[]
}>()

const { mutate: updateFilePrivacy, loading } = useUpdateFilePrivacyMutation()

const userStore = useUserStore()
const toastStore = useToastStore()

const isBasic = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Pro)
)

const containerClasses = computed(() => ({
  'pointer-events-none opacity-50': isBasic.value,
}))

const handleClick = async () => {
  try {
    const response = await updateFilePrivacy(
      {
        fileIds: props.ids,
        isPrivate: false,
      },
      {
        update(cache) {
          props.ids.forEach((id) => {
            updateFileInExplorerCache(id, {
              settings: { __typename: 'FileSettings', isPrivate: false },
            })
            cache.updateFragment(
              {
                id: `File:${id}`,
                fragment: gql`
                  fragment UpdatePrivacy on File {
                    id
                    isPrivate
                  }
                `,
              },
              (item) => {
                if (!item) return

                return {
                  ...item,
                  isPrivate: false,
                }
              }
            )
          })
        },
      }
    )

    if (response?.data?.updateFilePrivacy.asyncProcess) {
      openModal(Modals.Success, {
        props: {
          title: 'Bulk change successful',
          html: 'Note: In some cases, it may take several minutes to process your request.',
        },
      })
    } else {
      close()

      toastStore.showToast({
        type: ToastType.Success,
        content: 'Successfully updated file privacy settings',
      })
    }
  } catch {
    toastStore.showToast({
      type: ToastType.Error,
      content: 'Could not update privacy settings',
    })
  }
}
</script>
